/**
 * WaitingScreen — Armed state: retreat begins imminently.
 *
 * M2-UX-P1-012: Shown when a scheduled retreat is within the armed window.
 * UI-INV-005: Leaf component, props only, no engine imports.
 */
import { useState, useEffect } from "react";
import type { EditableRetreatSchedule } from "@retreat/core";
import { displayScheduleName, formatCountdown } from "../utils/scheduleUtils";
import styles from "./WaitingScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { SecondaryAction } from "../primitives/SecondaryAction";

type Props = {
  schedule?: EditableRetreatSchedule | null;
  scheduledAt: number;
  onCancel: () => void;
  onBeginNow: () => void;
  whisperLine?: string | null;
};

export function WaitingScreen({
  schedule,
  scheduledAt,
  onCancel,
  onBeginNow,
  whisperLine,
}: Props) {
  const [msRemaining, setMsRemaining] = useState(() =>
    Math.max(0, scheduledAt - Date.now()),
  );

  useEffect(() => {
    const tick = () => {
      const remaining = Math.max(0, scheduledAt - Date.now());
      setMsRemaining(remaining);
      if (remaining === 0) onBeginNow();
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [scheduledAt, onBeginNow]);

  const scheduledDate = new Date(scheduledAt);
  const timeLabel = scheduledDate.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const dayLabel = scheduledDate.toLocaleDateString(undefined, {
    weekday: "long",
  });

  return (
    <div className={styles.waiting}>
      <div className={styles.eyebrow}>
        Ready · {timeLabel} · {dayLabel}
      </div>

      <div className={styles.beginsIn}>Begins in</div>

      <div
        className={styles.countdown}
        aria-live="polite"
        aria-label="Time until retreat begins"
      >
        {formatCountdown(Date.now() + msRemaining).numeral}
      </div>

      <div className={styles.unit}>minutes · seconds</div>

      <div className={styles.info}>
        {schedule ? displayScheduleName(schedule) : "Your retreat"}
      </div>

      <div className={styles.instruction}>
        The retreat will begin automatically. Set the device aside.
      </div>

      {whisperLine && (
        <p className={styles.whisper}>{whisperLine}</p>
      )}

      <div className={styles.actions}>
        <SecondaryAction onClick={onCancel} testId={TEST_IDS.WAITING_CANCEL_BTN}>Cancel retreat</SecondaryAction>
      </div>
    </div>
  );
}
